/**
 * This method returns `undefined`.
 *
 * @static
 * @memberOf _
 * @since 2.3.0
 * @category Util
 * @example
 *
 * _.times(2, _.noop);
 * // => [undefined, undefined]
 */
function noop() {
  // No operation performed.
}

var domain = {
  JavaScript: 0,
  Protocol: 1,
  RemoteObject: 2,

  iOSX: 10,
  Android: 20,
  Windows: 30
};

var code = {
  generic: 0,
  eval: 1,
  range: 2,
  reference: 3,
  syntax: 4,
  type: 5,
  uri: 6,

  parse: 1,
  format: 2,
  missingProcedure: 3,
  invalidMessage: 4,
  oddResponse: 5,

  missingClass: 0,
  invalidInstance: 1,
  // missingProcedure: 2
  invalidArguments: 3,
  invalidModifier: 4
};

var jsErrors = [
  '', // Generic Error
  'Eval',
  'Range',
  'Reference',
  'Syntax',
  'Type',
  'URI'
];

//
var errorNames = [
  // JavaScript
  jsErrors,

  // Protocol
  [
    '',
    'Parse',
    'Format',
    'MissingProcedure',
    'InvalidMessageType',
    'OddResponse'
  ],

  // RemoteObject
  [
    'MissingClass',
    'InvalidInstance',
    'MissingProcedure',
    'InvalidArguments',
    'InvalidModifier'
  ]
];

var WisperError = function WisperError(domain, code, message, underlying, data) {
  var name;

  this.domain = domain;
  this.code = code;
  this.name = '';
  this.message = message;

  if (errorNames[domain] && (name = errorNames[domain][code]) != null) {
    this.name = name + 'Error';
  }

  this.underlying = underlying;
  this.data = data;
};

// Casts a `WisperError`-like object to an actual `WisperError`.
WisperError.cast = function cast (error) {
  if (error instanceof WisperError) { return error; }

  if (error instanceof Error) {
    return new WisperError(domain.JavaScript,
      jsErrors.indexOf(error.name.slice(0, -5)),
      error.message);
  }

  // It's a plain object.
  var err = new WisperError(
    error.domain, error.code, error.message,
    error.underlying ? WisperError.cast(error.underlying) : undefined,
    error.data);

  // Set the `name` property if the error name isn't locally available.
  err.name = err.name || error.name;

  return err;
};

// import WisperError from './error.es6';

// Checks whether the `msg` is a `wisper.protocol.Message`.
function isMessage(msg) {
  return msg && (isInvoke(msg) || isResponse(msg) || isPlainError(msg));
}


function isInvoke(msg) {
  return ('method' in msg && Array.isArray(msg.params));
}


function isPlainError(msg) {
  return 'error' in msg && !( 'id' in msg );
}


// Checks whether the `msg` is a `wisper.protocol.ResponseMessage`.
function isResponse(msg) {
  return 'id' in msg && ('result' in msg || 'error' in msg);
}


function isResult(msg) {
  return 'id' in msg && 'result' in msg;
}

var isChar = /[a-z_0-9]/i;

function split(path) {
  var n = path.length;
  var i = 0, ch = path[i];

  while (isChar.test(ch) && i < n) {
    ch = path[++i];
  }

  // Don't return ['', 'path'] except for '.path'.
  if ( i === 0 && ch !== '.' ) { return [path]; }

  return [path.slice(0, i), path.slice(ch === '.' ? i + 1 : i)];
}

var Namespace = function Namespace() {
  this.routes = Object.create(null);
};

Namespace.prototype.route = function route (path, msg) {
  if (!path) {
    return Promise.reject(new WisperError(domain.Protocol,
      code.missingProcedure, ("Invalid path '" + (msg.method) + "'!")));
  }

  var ref = split(path);
    var step = ref[0];
    var rest = ref[1];

  // Lookup the PathHandler.
  var handler = this.routes[ step ];

  if (!handler) {
    return Promise.reject(new WisperError(domain.Protocol,
      code.missingProcedure, ("No route for '" + (msg.method) + "'!")));
  }

  // Is it a Router?
  if (typeof handler.route === 'function') {
    return handler.route(rest, msg);
  }

  // It must be a RouteFunction.
  return handler(rest, msg);
};

// Tries to expose `handler` at the given `path`.
Namespace.prototype.expose = function expose (path, handler) {
  var ref = split(path);
    var step = ref[0];
    var rest = ref[1];

  // Does there currently exist a handler?
  var existing = this.routes[ step ];

  if (!existing) {

    if (!rest) {
      this.routes[ step ] = handler;
      return true;
    }

    existing = this.routes[ step ] = new Namespace();
  }

  if (existing instanceof Namespace) {
    return existing.expose(rest, handler);
  }

  return false;
};

// Create string id generating function.
function stringId() {
  var base = Math.random().toString(36).slice(2);
  var id = 0;

  return function () { return base + id++; };
}

var nextBridgeId = stringId();

function nullIfUndefined( value ) {
  return value != null ? value : null;
}

var BaseBridge = function BaseBridge() {
  this.id = this.constructor.name + nextBridgeId() + '-';
  this.count = 0;

  this.router = new Namespace();

  this.waiting = Object.create(null);

  // A meta property for attaching metadata.
  this.meta = Object.create(null);
};

/**
 * Returns a unique request id.
 *
 * @private
 * @return {string} id
 */
BaseBridge.prototype.nextId = function nextId () {
  return this.id + this.count++;
};

/**
 * Sends `msg` across the bridge.
 *
 * @private
 * @param{Message} msg
 */
BaseBridge.prototype.send = function send (msg) {
  this.sendJSON(JSON.stringify(msg));
};

/**
 * Invokes `method` with `params` as arguments.
 *
 * @param{string} method
 * @param{any[]} params
 * @return {Promise<?>}
 */
BaseBridge.prototype.invoke = function invoke (method, params) {
    var this$1 = this;
    if ( params === void 0 ) params=[];

  var id = this.nextId();

  return new Promise(function (resolve, reject) {
    // In the unlikely event that we get a synchronous response,
    // we'll have to be ready to receive it, or we'll error.
    this$1.waiting[ id ] = { resolve: resolve, reject: reject };

    // Send the message once we're waiting for the response.
    this$1.send({ method: method, params: params, id: id });
  });
};

/**
 * Notifies `method` with `params` as arguments.
 *
 * @param{string} method
 * @param{any[]} params
 */
BaseBridge.prototype.notify = function notify (method, params) {
    if ( params === void 0 ) params=[];

  this.send({ method: method, params: params });
};

/**
 * Invokes `method` with `params` as arguments.
 * All Promise arguments are resolved before invoking the method.
 *
 * @param{string} method
 * @param{any[]} params
 * @return {Promise<?>}
 */
BaseBridge.prototype.invokeAsync = function invokeAsync (method, params) {
    if ( params === void 0 ) params=[];

  return Promise.all(params).then(this.invoke.bind(this, method));
};

/**
 * Notifies `method` with `params` as arguments.
 * All Promise arguments are resolved before sending the notification.
 *
 * @param{string} method
 * @param{any[]} params
 */
BaseBridge.prototype.notifyAsync = function notifyAsync (method, params) {
    if ( params === void 0 ) params=[];

  Promise.all(params).then(this.notify.bind(this, method));
};

/**
 * Receive a message encoded as a JSON string.
 *
 * @param{string} json
 */
BaseBridge.prototype.receiveJSON = function receiveJSON (json) {
  try {
    this.receive(JSON.parse(json));
  } catch (e) {
    this.send({ error: WisperError.cast(e) });
  }
};

/**
 * Receive a message, by interpreting the given object as a message if possible.
 *
 * @param{Object} msg
 */
BaseBridge.prototype.receive = function receive (msg) {
  if (!isMessage(msg)) {
    this.send({
      error: new WisperError(domain.Protocol, code.format, ("Invalid message format, message keys: " + (Object.keys( msg )))),
    });
    return;
  }

  if (isResponse(msg)) {
    this.handleResponse(msg);
  } else if (isPlainError(msg)) {
    // TODO: Improve error handling. Log for now.
    console.error(msg.error);
  } else {
    this.sendResponse(msg.id, this.router.route(msg.method, msg));
  }
};

/**
 * Sends a response for a request with `id`, if `promise` isn't null.
 *
 * @param{string} id
 * @param{Promise?} promise
 */
BaseBridge.prototype.sendResponse = function sendResponse (id, promise) {
    var this$1 = this;

  if (promise) {
    promise.then( nullIfUndefined ).then(
      function (result) { return id && this$1.send({ id: id, result: result }); },
      function (error) { return this$1.send({ id: id, error: error }); });
  }
};

/**
 * Handle a response for a request awaiting one.
 *
 * @param{ResponseMessage} msg
 */
BaseBridge.prototype.handleResponse = function handleResponse (msg) {
  var waiting = this.waiting[ msg.id ];

  delete this.waiting[ msg.id ];

  if (waiting) {
    if (isResult(msg)) {
      waiting.resolve(msg.result);
    } else {
      waiting.reject(WisperError.cast(msg.error));
    }
  } else {
    this.send({
      id: msg.id,
      error: new WisperError(domain.Protocol, code.oddResponse,
      ("Got a response for id: '" + (msg.id) + "', but no request was made.")),
    });
  }
};

/**
 * Exposes a route handler at a given path.
 * @param{string} path
 * @param{RouteHandler} handler
 * @return {boolean} whether the expose was successful
 */
BaseBridge.prototype.expose = function expose (path, handler) {
  return this.router.expose(path, handler);
};

/**
 * Exposes a function wrapped in a route handler at a given path.
 * @param{string} path
 * @param{function} exposed function
 * @return {boolean} whether the expose was successful
 */
BaseBridge.prototype.exposeFunction = function exposeFunction (path, fn) {
  return this.expose(path, function (path, message) {
    return Promise.resolve(fn.apply(null, message.params));
  });
};


/**
 * Closes the bridge preventing any more messages from being sent.
 */
BaseBridge.prototype.close = function close () {
  this.sendJSON = noop;
};

var IframeBridge = (function (BaseBridge) {
  function IframeBridge(targetWindow) {
    BaseBridge.call(this);
    this.target = targetWindow;
    window.addEventListener('message', this);
  }

  if ( BaseBridge ) IframeBridge.__proto__ = BaseBridge;
  IframeBridge.prototype = Object.create( BaseBridge && BaseBridge.prototype );
  IframeBridge.prototype.constructor = IframeBridge;

  /**
   * Sends JSON by invoking `targetWindow.postMessage`.
   *
   * @private
   * @override
   * @param {string} json
   */
  IframeBridge.prototype.sendJSON = function sendJSON (json) {
    this.target.postMessage(json, '*');
  };

  /**
   * Handles messages sent to this window. If the source of the Event
   * is our target window, the data is routed into the bridge.
   *
   * @private
   * @param {Event} msg
   */
  IframeBridge.prototype.handleEvent = function handleEvent (msg) {
    if (msg.source === this.target) {
      this.receiveJSON(msg.data);
    }
  };

  IframeBridge.prototype.close = function close () {
    BaseBridge.prototype.close.call(this);
    window.removeEventListener('message', this);
  };

  return IframeBridge;
}(BaseBridge));

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof global == 'object' && global && global.Object === Object && global;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/** Built-in value references. */
var Symbol = root.Symbol;

/** Used for built-in method references. */
var objectProto$2 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$2.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto$2.toString;

/** Built-in value references. */
var symToStringTag$1 = Symbol ? Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty$2.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$3.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString$1.call(value);
}

var nullTag = '[object Null]';
var undefinedTag = '[object Undefined]';
/** Built-in value references. */
var symToStringTag = Symbol ? Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var asyncTag = '[object AsyncFunction]';
var funcTag = '[object Function]';
var genTag = '[object GeneratorFunction]';
var proxyTag = '[object Proxy]';
/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  if (!isObject(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = baseGetTag(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}

/** Used to detect overreaching core-js shims. */
var coreJsData = root['__core-js_shared__'];

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

/** Used for built-in method references. */
var funcProto$1 = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString$1.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto = Function.prototype;
var objectProto$1 = Object.prototype;
/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$1.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString.call(hasOwnProperty$1).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject(value) || isMasked(value)) {
    return false;
  }
  var pattern = isFunction(value) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource(value));
}

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = getValue(object, key);
  return baseIsNative(value) ? value : undefined;
}

var defineProperty = (function() {
  try {
    var func = getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue(object, key, value) {
  if (key == '__proto__' && defineProperty) {
    defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty.call(object, key) && eq(objValue, value)) ||
      (value === undefined && !(key in object))) {
    baseAssignValue(object, key, value);
  }
}

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && baseGetTag(value) == symbolTag);
}

var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/;
var reIsPlainProp = /^\w*$/;
/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (isArray(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

/* Built-in method references that are verified to be native. */
var nativeCreate = getNative(Object, 'create');

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = nativeCreate ? nativeCreate(null) : {};
  this.size = 0;
}

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty$3.call(data, key) ? data[key] : undefined;
}

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$5.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return nativeCreate ? (data[key] !== undefined) : hasOwnProperty$4.call(data, key);
}

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (nativeCreate && value === undefined) ? HASH_UNDEFINED$1 : value;
  return this;
}

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var this$1 = this;

  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this$1.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash.prototype.clear = hashClear;
Hash.prototype['delete'] = hashDelete;
Hash.prototype.get = hashGet;
Hash.prototype.has = hashHas;
Hash.prototype.set = hashSet;

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return assocIndexOf(this.__data__, key) > -1;
}

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var this$1 = this;

  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this$1.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache.prototype.clear = listCacheClear;
ListCache.prototype['delete'] = listCacheDelete;
ListCache.prototype.get = listCacheGet;
ListCache.prototype.has = listCacheHas;
ListCache.prototype.set = listCacheSet;

/* Built-in method references that are verified to be native. */
var Map = getNative(root, 'Map');

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new Hash,
    'map': new (Map || ListCache),
    'string': new Hash
  };
}

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  var result = getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return getMapData(this, key).get(key);
}

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return getMapData(this, key).has(key);
}

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  var data = getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var this$1 = this;

  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this$1.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache.prototype.clear = mapCacheClear;
MapCache.prototype['delete'] = mapCacheDelete;
MapCache.prototype.get = mapCacheGet;
MapCache.prototype.has = mapCacheHas;
MapCache.prototype.set = mapCacheSet;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize.Cache || MapCache);
  return memoized;
}

// Expose `MapCache`.
memoize.Cache = MapCache;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped(func) {
  var result = memoize(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });

  var cache = result.cache;
  return result;
}

var reLeadingDot = /^\./;
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;
/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath = memoizeCapped(function(string) {
  var result = [];
  if (reLeadingDot.test(string)) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, string) {
    result.push(quote ? string.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function arrayMap(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto = Symbol ? Symbol.prototype : undefined;
var symbolToString = symbolProto ? symbolProto.toString : undefined;
/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return arrayMap(value, baseToString) + '';
  }
  if (isSymbol(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
}

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : baseToString(value);
}

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath(value, object) {
  if (isArray(value)) {
    return value;
  }
  return isKey(value, object) ? [value] : stringToPath(toString(value));
}

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  length = length == null ? MAX_SAFE_INTEGER : length;
  return !!length &&
    (typeof value == 'number' || reIsUint.test(value)) &&
    (value > -1 && value % 1 == 0 && value < length);
}

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value == 'string' || isSymbol(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$1) ? '-0' : result;
}

/**
 * The base implementation of `_.set`.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {Array|string} path The path of the property to set.
 * @param {*} value The value to set.
 * @param {Function} [customizer] The function to customize path creation.
 * @returns {Object} Returns `object`.
 */
function baseSet(object, path, value, customizer) {
  if (!isObject(object)) {
    return object;
  }
  path = castPath(path, object);

  var index = -1,
      length = path.length,
      lastIndex = length - 1,
      nested = object;

  while (nested != null && ++index < length) {
    var key = toKey(path[index]),
        newValue = value;

    if (index != lastIndex) {
      var objValue = nested[key];
      newValue = customizer ? customizer(objValue, key, nested) : undefined;
      if (newValue === undefined) {
        newValue = isObject(objValue)
          ? objValue
          : (isIndex(path[index + 1]) ? [] : {});
      }
    }
    assignValue(nested, key, newValue);
    nested = nested[key];
  }
  return object;
}

/**
 * Sets the value at `path` of `object`. If a portion of `path` doesn't exist,
 * it's created. Arrays are created for missing index properties while objects
 * are created for all other missing properties. Use `_.setWith` to customize
 * `path` creation.
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to modify.
 * @param {Array|string} path The path of the property to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns `object`.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.set(object, 'a[0].b.c', 4);
 * console.log(object.a[0].b.c);
 * // => 4
 *
 * _.set(object, ['x', '0', 'y', 'z'], 5);
 * console.log(object.x[0].y.z);
 * // => 5
 */
function set(object, path, value) {
  return object == null ? object : baseSet(object, path, value);
}

var PropertyBridge = (function (BaseBridge) {
  function PropertyBridge(target, receiveProperty, send) {
    var this$1 = this;

    BaseBridge.call(this);
    set( this.target = target, this.receiveProperty = receiveProperty, function (json) {
      this$1.receiveJSON(json);
    });
    this.sendJSON = send;
  }

  if ( BaseBridge ) PropertyBridge.__proto__ = BaseBridge;
  PropertyBridge.prototype = Object.create( BaseBridge && BaseBridge.prototype );
  PropertyBridge.prototype.constructor = PropertyBridge;

  PropertyBridge.prototype.close = function close () {
    BaseBridge.prototype.close.call(this);
    set(this.target, this.receiveProperty, null);
  };

  return PropertyBridge;
}(BaseBridge));

/**
 * Creates a base function for methods like `_.forIn` and `_.forOwn`.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseFor(fromRight) {
  return function(object, iteratee, keysFunc) {
    var index = -1,
        iterable = Object(object),
        props = keysFunc(object),
        length = props.length;

    while (length--) {
      var key = props[fromRight ? length : ++index];
      if (iteratee(iterable[key], key, iterable) === false) {
        break;
      }
    }
    return object;
  };
}

/**
 * The base implementation of `baseForOwn` which iterates over `object`
 * properties returned by `keysFunc` and invokes `iteratee` for each property.
 * Iteratee functions may exit iteration early by explicitly returning `false`.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @returns {Object} Returns `object`.
 */
var baseFor = createBaseFor();

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

/** `Object#toString` result references. */
var argsTag = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments(value) {
  return isObjectLike(value) && baseGetTag(value) == argsTag;
}

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$7.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$7.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
  return isObjectLike(value) && hasOwnProperty$6.call(value, 'callee') &&
    !propertyIsEnumerable.call(value, 'callee');
};

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}

/** Detect free variable `exports`. */
var freeExports = typeof exports == 'object' && exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && typeof module == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? root.Buffer : undefined;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */
var isBuffer = nativeIsBuffer || stubFalse;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$1;
}

var argsTag$1 = '[object Arguments]';
var arrayTag = '[object Array]';
var boolTag = '[object Boolean]';
var dateTag = '[object Date]';
var errorTag = '[object Error]';
var funcTag$1 = '[object Function]';
var mapTag = '[object Map]';
var numberTag = '[object Number]';
var objectTag = '[object Object]';
var regexpTag = '[object RegExp]';
var setTag = '[object Set]';
var stringTag = '[object String]';
var weakMapTag = '[object WeakMap]';
var arrayBufferTag = '[object ArrayBuffer]';
var dataViewTag = '[object DataView]';
var float32Tag = '[object Float32Array]';
var float64Tag = '[object Float64Array]';
var int8Tag = '[object Int8Array]';
var int16Tag = '[object Int16Array]';
var int32Tag = '[object Int32Array]';
var uint8Tag = '[object Uint8Array]';
var uint8ClampedTag = '[object Uint8ClampedArray]';
var uint16Tag = '[object Uint16Array]';
var uint32Tag = '[object Uint32Array]';
/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag] =
typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
typedArrayTags[errorTag] = typedArrayTags[funcTag$1] =
typedArrayTags[mapTag] = typedArrayTags[numberTag] =
typedArrayTags[objectTag] = typedArrayTags[regexpTag] =
typedArrayTags[setTag] = typedArrayTags[stringTag] =
typedArrayTags[weakMapTag] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray(value) {
  return isObjectLike(value) &&
    isLength(value.length) && !!typedArrayTags[baseGetTag(value)];
}

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function(value) {
    return func(value);
  };
}

/** Detect free variable `exports`. */
var freeExports$1 = typeof exports == 'object' && exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule$1 = freeExports$1 && typeof module == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports$1 = freeModule$1 && freeModule$1.exports === freeExports$1;

/** Detect free variable `process` from Node.js. */
var freeProcess = moduleExports$1 && freeGlobal.process;

/** Used to access faster Node.js helpers. */
var nodeUtil = (function() {
  try {
    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}());

/* Node.js helper references. */
var nodeIsTypedArray = nodeUtil && nodeUtil.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$6.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys(value, inherited) {
  var isArr = isArray(value),
      isArg = !isArr && isArguments(value),
      isBuff = !isArr && !isArg && isBuffer(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$5.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           isIndex(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

/** Used for built-in method references. */
var objectProto$9 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$9;

  return value === proto;
}

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys = overArg(Object.keys, Object);

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$8.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys(object) {
  if (!isPrototype(object)) {
    return nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$7.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength(value.length) && !isFunction(value);
}

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys(object) {
  return isArrayLike(object) ? arrayLikeKeys(object) : baseKeys(object);
}

/**
 * The base implementation of `_.forOwn` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Object} Returns `object`.
 */
function baseForOwn(object, iteratee) {
  return object && baseFor(object, iteratee, keys);
}

function mapValues(object, iteratee) {
  var result = {};
  // iteratee = baseIteratee(iteratee, 3);

  baseForOwn(object, function (value, key, object) {
    result[ key ] = iteratee(value, key, object);
  });
  return result;
}

// Like `clone` but hackier, simpler and good enough.
function clonePlain(object) {
  return JSON.parse( JSON.stringify( object ) );
}

/**
 * Creates a `baseEach` or `baseEachRight` function.
 *
 * @private
 * @param {Function} eachFunc The function to iterate over a collection.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseEach(eachFunc, fromRight) {
  return function(collection, iteratee) {
    if (collection == null) {
      return collection;
    }
    if (!isArrayLike(collection)) {
      return eachFunc(collection, iteratee);
    }
    var length = collection.length,
        index = fromRight ? length : -1,
        iterable = Object(collection);

    while ((fromRight ? index-- : ++index < length)) {
      if (iteratee(iterable[index], index, iterable) === false) {
        break;
      }
    }
    return collection;
  };
}

/**
 * The base implementation of `_.forEach` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array|Object} Returns `collection`.
 */
var baseEach = createBaseEach(baseForOwn);

/**
 * The base implementation of `_.every` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if all elements pass the predicate check,
 *  else `false`
 */
function baseEvery(collection, predicate) {
  var result = true;
  baseEach(collection, function(value, index, collection) {
    result = !!predicate(value, index, collection);
    return result;
  });
  return result;
}

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */
function identity(value) {
  return value;
}

// A `type` has the following properties:
//   name         : string
//   writable     : boolean
//   instance     : boolean
//   defaultValue : () => type
//   default      : (type) => this
//   valid        : (type) => boolean
//   marshal      : (type) => ?
//   unmarshal    : (?) => type
//
// The `default` method is used to create a new type with
// the given value as the default. The given value must be
// a valid value of the type.
//
// ```js
// console.log(number.defaultValue() === 0);
//
// let one = number.default(1);
// console.log(one.defaultValue() === 1);
//
// let two = number.default('2'); // throws TypeError
// ```

var invalidBaseType = new TypeError('Invalid base type.');


// The `any` type is at the top of the hierarchy.
// All values are valid, but it has no default value.
var any = {
  writable: true,
  instance: false,

  name: 'any',

  valid: function valid() {
    return true;
  },

  // Creates a new type, with `val` as the type's the default value.
  default: function default$1(val) {
    if (!this.valid(val)) {
      throw new TypeError(("Invalid default value for '" + (this.name) + "'!"));
    }

    var type = Object.create(this);

    type.defaultValue = function () { return clonePlain(val); };

    return type;
  },

  marshal: identity,

  unmarshal: identity
};


// Check if the argument is a valid type.
function isType(type) {
  if (!type) {
    return false;
  }

  if (type === any) {
    return true;
  }

  return type === Object(type) && isType(Object.getPrototypeOf(type));
}


// Creates a primitive type from a name and a default value.
function primitive(name, defaultValue) {
  var type = Object.create(any);

  type.name = name;
  type.defaultValue = function () { return defaultValue; };
  type.valid = function (val) { return typeof val === name; };

  return type;
}


var string = primitive('string', '');
var number = primitive('number', 0);
var boolean = primitive('boolean', false);


// Modifies the given `baseType` to be read-only.
function readonly(baseType) {
  if (!isType(baseType)) {
    throw invalidBaseType;
  }

  var type = Object.create(baseType);

  type.name = "readonly<" + (baseType.name) + ">";
  type.writable = false;

  return type;
}


// Creates a type that is the array of the given `baseType`.
function array(baseType) {
  if (!isType(baseType)) {
    throw invalidBaseType;
  }

  var type = Object.create(baseType);

  type.name = "array<" + (baseType.name) + ">";
  type.valid = function (val) { return Array.isArray(val) && val.every(baseType.valid); };
  type.defaultValue = function () { return []; };

  type.marshal = function (arr) { return Promise.all( arr.map( baseType.marshal ) ); };
  type.unmarshal = function (arr) { return arr.map( baseType.unmarshal ); };

  return type;
}


// Creates a new composite type of the given object structure.
// The given value must be an object with only properties.
function object(properties) {
  if (!isObjectLike(properties) || !baseEvery(properties, isType)) {
    throw new TypeError('Not all object properties were types.');
  }

  var type = Object.create(any);

  type.name = '{ ' + Object.keys(properties).map(function (key) { return key + ': ' + properties[key].name; }).join(', ') + ' }';

  type.valid = function (val) { return isObjectLike(val) &&
    baseEvery(properties, function (baseType, key) { return baseType.valid(val[key]); }); };

  type.defaultValue = function () { return mapValues(properties, function (baseType) { return baseType.defaultValue(); }); };

  type.marshal = function (val) { return mapValues(properties, function (baseType, key) { return baseType.marshal( val[key] ); }); };
  type.unmarshal = function (val) { return mapValues(properties, function (baseType, key) { return baseType.unmarshal( val[key] ); }); };

  return type;
}


// Creates a type that is nullable of the given `baseType`.
function nullable(baseType) {
  if (!isType(baseType)) {
    throw invalidBaseType;
  }

  var type = Object.create(baseType);

  type.name = "nullable<" + (baseType.name) + ">";
  type.valid = function (val) { return val === null || baseType.valid(val); };
  type.defaultValue = function () { return null; };

  type.marshal = function (val) { return val === null ? val : baseType.marshal(val); };
  type.unmarshal = function (val) { return val === null ? val : baseType.unmarshal(val); };

  return type;
}


// Creates a type that is satisfied by instances of `cls`.
function instance(cls) {
  if (typeof cls !== 'function') {
    throw invalidBaseType;
  }

  var type = Object.create(any);

  type.instance = true;
  type.name = "instance<" + (cls.name) + ">";
  type.valid = function (val) { return val instanceof cls; };
  type.defaultValue = function () { return null; };

  type.marshal = function (obj) { return obj.id; };
  type.unmarshal = function (id) { return cls.instances[id]; };

  return type;
}


var types = Object.freeze({
  any: any,
  isType: isType,
  string: string,
  number: number,
  boolean: boolean,
  readonly: readonly,
  array: array,
  object: object,
  nullable: nullable,
  instance: instance
});

// Decorator to define the type `signature` for the decorated class or method.
function signature(parameterTypes, returnType) {
  if (!Array.isArray(parameterTypes) || !parameterTypes.every(isType) ||
      (returnType && !isType(returnType))) {
    throw new TypeError('`signature` takes array of parameter types, and an optional return type.');
  }

  return function (target, key, descriptor) {
    // Methods decorators receive three parameters.
    // Class decorators receive only one.
    var func = descriptor ? descriptor.value : target;

    func.parameterTypes = parameterTypes;
    func.returnType = returnType;
  };
}

// TODO: Change `asap` to something more effective?
var asap = setTimeout;

function EventHandler() {
  var allHandlers = {};

  /**
   * @param {string} type
   * @param {Function} func
   */
  this.on = this.addEventListener = function (type, func) {
    var handlers = allHandlers[ type ];

    if (handlers) {
      handlers.push(func);
    } else {
      allHandlers[ type ] = [ func ];
    }

    return this;
  };

  /**
   * @param {string} type
   * @param {Function} func
   */
  this.off = this.removeEventListener = function (type, func) {
    var handlers = allHandlers[ type ];

    if (handlers) {
      handlers.splice(handlers.indexOf(func), 1);
    }

    return this;
  };

  /**
   * @param {string} type
   * @param {Object=} data
   */
  this.emit = this.dispatchEvent = function (type, data) {
    var handlers = allHandlers[ type ];

    if (handlers) {
      handlers = handlers.slice();

      asap(function () {
        handlers.forEach(function (handler) {
          handler(data);
        });
      });
    }

    return this;
  };

  return this;
}

/**
 * Copies properties of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy properties from.
 * @param {Array} props The property identifiers to copy.
 * @param {Object} [object={}] The object to copy properties to.
 * @param {Function} [customizer] The function to customize copied values.
 * @returns {Object} Returns `object`.
 */
function copyObject(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});

  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];

    var newValue = customizer
      ? customizer(object[key], source[key], key, object, source)
      : undefined;

    if (newValue === undefined) {
      newValue = source[key];
    }
    if (isNew) {
      baseAssignValue(object, key, newValue);
    } else {
      assignValue(object, key, newValue);
    }
  }
  return object;
}

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */
function apply(func, thisArg, args) {
  switch (args.length) {
    case 0: return func.call(thisArg);
    case 1: return func.call(thisArg, args[0]);
    case 2: return func.call(thisArg, args[0], args[1]);
    case 3: return func.call(thisArg, args[0], args[1], args[2]);
  }
  return func.apply(thisArg, args);
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max;

/**
 * A specialized version of `baseRest` which transforms the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @param {Function} transform The rest array transform.
 * @returns {Function} Returns the new function.
 */
function overRest(func, start, transform) {
  start = nativeMax(start === undefined ? (func.length - 1) : start, 0);
  return function() {
    var args = arguments,
        index = -1,
        length = nativeMax(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }
    index = -1;
    var otherArgs = Array(start + 1);
    while (++index < start) {
      otherArgs[index] = args[index];
    }
    otherArgs[start] = transform(array);
    return apply(func, this, otherArgs);
  };
}

/**
 * Creates a function that returns `value`.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {*} value The value to return from the new function.
 * @returns {Function} Returns the new constant function.
 * @example
 *
 * var objects = _.times(2, _.constant({ 'a': 1 }));
 *
 * console.log(objects);
 * // => [{ 'a': 1 }, { 'a': 1 }]
 *
 * console.log(objects[0] === objects[1]);
 * // => true
 */
function constant(value) {
  return function() {
    return value;
  };
}

/**
 * The base implementation of `setToString` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var baseSetToString = !defineProperty ? identity : function(func, string) {
  return defineProperty(func, 'toString', {
    'configurable': true,
    'enumerable': false,
    'value': constant(string),
    'writable': true
  });
};

/** Used to detect hot functions by number of calls within a span of milliseconds. */
var HOT_COUNT = 800;
var HOT_SPAN = 16;
/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeNow = Date.now;

/**
 * Creates a function that'll short out and invoke `identity` instead
 * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
 * milliseconds.
 *
 * @private
 * @param {Function} func The function to restrict.
 * @returns {Function} Returns the new shortable function.
 */
function shortOut(func) {
  var count = 0,
      lastCalled = 0;

  return function() {
    var stamp = nativeNow(),
        remaining = HOT_SPAN - (stamp - lastCalled);

    lastCalled = stamp;
    if (remaining > 0) {
      if (++count >= HOT_COUNT) {
        return arguments[0];
      }
    } else {
      count = 0;
    }
    return func.apply(undefined, arguments);
  };
}

/**
 * Sets the `toString` method of `func` to return `string`.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var setToString = shortOut(baseSetToString);

/**
 * The base implementation of `_.rest` which doesn't validate or coerce arguments.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @returns {Function} Returns the new function.
 */
function baseRest(func, start) {
  return setToString(overRest(func, start, identity), func + '');
}

/**
 * Checks if the given arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
 *  else `false`.
 */
function isIterateeCall(value, index, object) {
  if (!isObject(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number'
        ? (isArrayLike(object) && isIndex(index, object.length))
        : (type == 'string' && index in object)
      ) {
    return eq(object[index], value);
  }
  return false;
}

/**
 * Creates a function like `_.assign`.
 *
 * @private
 * @param {Function} assigner The function to assign values.
 * @returns {Function} Returns the new assigner function.
 */
function createAssigner(assigner) {
  return baseRest(function(object, sources) {
    var index = -1,
        length = sources.length,
        customizer = length > 1 ? sources[length - 1] : undefined,
        guard = length > 2 ? sources[2] : undefined;

    customizer = (assigner.length > 3 && typeof customizer == 'function')
      ? (length--, customizer)
      : undefined;

    if (guard && isIterateeCall(sources[0], sources[1], guard)) {
      customizer = length < 3 ? undefined : customizer;
      length = 1;
    }
    object = Object(object);
    while (++index < length) {
      var source = sources[index];
      if (source) {
        assigner(object, source, index, customizer);
      }
    }
    return object;
  });
}

/** Used for built-in method references. */
var objectProto$10 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$10.hasOwnProperty;

/**
 * Assigns own enumerable string keyed properties of source objects to the
 * destination object. Source objects are applied from left to right.
 * Subsequent sources overwrite property assignments of previous sources.
 *
 * **Note:** This method mutates `object` and is loosely based on
 * [`Object.assign`](https://mdn.io/Object/assign).
 *
 * @static
 * @memberOf _
 * @since 0.10.0
 * @category Object
 * @param {Object} object The destination object.
 * @param {...Object} [sources] The source objects.
 * @returns {Object} Returns `object`.
 * @see _.assignIn
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * function Bar() {
 *   this.c = 3;
 * }
 *
 * Foo.prototype.b = 2;
 * Bar.prototype.d = 4;
 *
 * _.assign({ 'a': 0 }, new Foo, new Bar);
 * // => { 'a': 1, 'c': 3 }
 */
var assign = createAssigner(function(object, source) {
  if (isPrototype(source) || isArrayLike(source)) {
    copyObject(source, keys(source), object);
    return;
  }
  for (var key in source) {
    if (hasOwnProperty$8.call(source, key)) {
      assignValue(object, key, source[key]);
    }
  }
});

var internal = '_repr_';

var Base = (function (EventHandler) {
  function Base() {
    EventHandler.call(this);

    // Create the instance's `internal` property.
    this[internal] = Object.create(this[internal], {
      props: {
        enumerable: true,

        // Set defaults.
        value: mapValues(this[internal].props, function (type) { return type.defaultValue(); })
      }
    });
  }

  if ( EventHandler ) Base.__proto__ = EventHandler;
  Base.prototype = Object.create( EventHandler && EventHandler.prototype );
  Base.prototype.constructor = Base;


  // Dispatch an event from this instance across the bridge.
  Base.prototype.dispatch = function dispatch (type, value) {
    this.bridge.notifyAsync(this.interfaceName + ':!', [this.id, type, value]);
  };

  return Base;
}(EventHandler));

// Give `Base` all methods of an `EventHandler`,
// in turn handing them down to all subclasses.
var methods = Object.getOwnPropertyNames(EventHandler.prototype)
  .reduce(function (object, name) {
    object[name] = name;
    return object;
  }, Object.create(null));

Object.defineProperties(Base, mapValues(methods, function (name) { return Object.getOwnPropertyDescriptor(EventHandler.prototype, name); }));

Base.prototype[internal] = Object.create(Object.prototype);

var nextId = stringId();


var Local = (function (Base) {
  function Local() {
    Base.call(this);
    this.bridge = null;
    this.interfaceName = '';

    this.id = Promise.resolve(this[internal].id = nextId());
  }

  if ( Base ) Local.__proto__ = Base;
  Local.prototype = Object.create( Base && Base.prototype );
  Local.prototype.constructor = Local;

  var prototypeAccessors = { ready: {} };

  Local.routerThrough = function routerThrough (bridge) {
    // `this.routers` is dynamically created by `interfaceName`.
    return this.routers[bridge.id];
  };

  prototypeAccessors.ready.get = function () {
    return Promise.resolve(this);
  };

  prototypeAccessors.ready.set = function (value) {
    Object.defineProperty(this, 'ready', { value: value });
  };

  Object.defineProperties( Local.prototype, prototypeAccessors );

  return Local;
}(Base));

// Returns true if `string` ends with `tail`.
function endsWith(string, tail) {
  return string.slice(-tail.length) === tail;
}

// TODO: return null if invalid modifiers
function parse(path) {
  var instance = path[0] === ':',
    event = endsWith(path, '!'),
    tilde = endsWith(path, '~'),
    method = (event || tilde) ? null : path.slice(instance);

  return { method: method, event: event, instance: instance, tilde: tilde };
}


// Returns `value[internal].id` if it exists, otherwise `value`.
function internalIds(value) {
  if (value && value[internal]) {
    return value[internal].id;
  }
  return value;
}


// Like `Function.prototype.apply`,
// returns a Promise, or null if `func` returned undefined.
function promisedApply(func, ctx, args) {
  try {
    var result = func.apply(ctx, args);

    return result !== undefined ? Promise.resolve(result) : null;
  } catch (e) {
    return Promise.reject(WisperError.cast(e));
  }
}


// Returns a Promise by calling `new Constructor(...args)`.
function promisedConstruct(Constructor, args) {
  try {
    return new (Function.prototype.bind.apply( Constructor, [ null ].concat( args) ));
  } catch (e) {
    return Promise.reject(WisperError.cast(e));
  }
}


// Returns a Promise for type-checked arguments, or a rejected promise.
function unmarshalAndTypeCheckArguments(func, args) {
  var types = func.parameterTypes,
    n = types ? types.length : 0,
    newArgs = args.slice(0);

  for (var i = 0; i < n; i += 1) {
    var type = types[i];
    var arg = args[i];

    // Unmarshal arguments to values corresponding to their respective types
    try {
      newArgs[i] = type.unmarshal(arg);

      // ... and type-check.
      if (!type.valid(newArgs[i])) {
        return Promise.reject(new WisperError(
          domain.RemoteObject,
          code.invalidArguments,
          ("Expected argument #" + (i + 1) + " to be of type '" + (type.name) + "', got: " + (JSON.stringify(args[i])) + ".")
        ));
      }
    } catch (e) {
      return Promise.reject(new WisperError(
        domain.RemoteObject,
        code.invalidInstance,
        ("No instance with id '" + (args[i]) + "'.")
      ));
    }
  }

  return Promise.resolve(newArgs);
}


var ClassRouter = function ClassRouter(bridge, name, cls) {
  if (!bridge.meta.instances) {
    bridge.meta.instances = Object.create(null);
  }

  this.bridge = bridge;
  this.name = name;
  this.cls = cls;
  this.instances = bridge.meta.instances;
};


// Returns a ClassRouter to do routing for `cls`.
ClassRouter.routing = function routing (bridge, name, cls) {
  if (cls.prototype instanceof Local) {
    return new LocalClassRouter(bridge, name, cls);
  }

  return new RemoteClassRouter(bridge, name, cls);
};


ClassRouter.prototype.route = function route (path, msg) {
  var target = parse(path);

  if (!target) {
    // TODO: proper error message
    return Promise.reject(new WisperError(domain.RemoteObject,
      code.invalidModifier, ''));
  }

  // Is the target an instance or the class?
  return target.instance ?
    this.instanceRoute(target, msg) :
    this.staticRoute(target, msg);
};


ClassRouter.prototype.instanceRoute = function instanceRoute (target, msg) {
  if (!msg.params.length) {
    return Promise.reject(new WisperError(domain.RemoteObject,
      code.invalidArguments, 'Missing instance id.'));
  }

  var id = msg.params[0],
    instance = this.instances[id];

  if (!(instance instanceof this.cls)) {
    return Promise.reject(new WisperError(domain.RemoteObject,
      code.invalidInstance, ("No instance with id '" + id + "'.")));
  }

  if (target.tilde) {
    return this.destroyInstance(instance);
  }

  if (target.event) {
    if (msg.params.length !== 3) {
      return Promise.reject(new WisperError(domain.RemoteObject,
        code.invalidArguments, ("Instance events requires 3 parameters, got: " + (msg.params.length))));;
    }
    return this.instanceEvent(instance, msg.params[1], msg.params[2]);
  }

  return this.instanceMethod(instance, target.method, msg.params.slice(1));
};


// Destroy the given `instance`.
ClassRouter.prototype.destroyInstance = function destroyInstance () {
  return Promise.reject(new WisperError(domain.RemoteObject,
    code.generic, 'UNIMPLEMENTED: instance destruction'));
};


// Dispatch an event on `instance` with the given `key` and `value`.
ClassRouter.prototype.instanceEvent = function instanceEvent (instance, key, value) {
  var props = instance[internal].props;

  // If the instance has an internal property `key`,
  // check the type and set its value.
  if (key in props) {
    var type = this.cls.prototype[internal].props[key];

    value = type.unmarshal(value);

    // Don't set the property or dispatch the event
    // if the value has the wrong type.
    if (!type.valid(value)) { return; }

    props[key] = value;
  }

  instance.emit(key, value);
};


// Call `instance`'s `method` with `args`.
ClassRouter.prototype.instanceMethod = function instanceMethod (instance, method) {
  return Promise.reject(new WisperError(domain.RemoteObject,
    code.missingProcedure,
    ("'" + (this.name) + "' instances have no method '" + method + "'.")));
};


ClassRouter.prototype.staticRoute = function staticRoute (target, msg) {
  if (target.tilde) {
    return this.constructInstance(msg.params);
  }

  if (target.event) {
    if (msg.params.length !== 2) {
      return Promise.reject(new WisperError(domain.RemoteObject,
        code.invalidArguments, ("Static events requires 2 parameters, got: " + (msg.params.length))));
    }
    return this.staticEvent(msg.params[0], msg.params[1]);
  }

  return this.staticFunction(target.method, msg.params);
};


// Construct an instance of `this.cls` with the given `args`.
ClassRouter.prototype.constructInstance = function constructInstance () {
  return Promise.reject(new WisperError(domain.RemoteObject,
    code.generic, // TODO: add `private-constructor` error code
    ("The constructor for " + (this.name) + " is private.")
  ));
};


// Dispatch an event on `this.cls` with the given `key` and `value`.
ClassRouter.prototype.staticEvent = function staticEvent (key, value) {
  // Can't know the type of the value of a plain event.
  this.cls.emit(key, value);
};


ClassRouter.prototype.staticFunction = function staticFunction (method, plainArgs) {
    var this$1 = this;

  var func = this.cls[method];

  if (typeof func !== 'function' || func.secret) {
    return Promise.reject(new WisperError(domain.RemoteObject,
      code.missingProcedure,
      ("'" + (this.name) + "' has no static method '" + method + "'.")));
  }

  return unmarshalAndTypeCheckArguments(func, plainArgs)
    .then( function (args) { return promisedApply(func, this$1.cls, args); })
    .then( func.returnType.marshal );
};

var RemoteClassRouter = (function (ClassRouter) {
  function RemoteClassRouter() {
      ClassRouter.apply(this, arguments);

      this.cls.instances = this.instances;

      Object.defineProperties(this.cls.prototype, {
        interfaceName: { value: this.name },
        bridge: { value: this.bridge }
      });
    }

  if ( ClassRouter ) RemoteClassRouter.__proto__ = ClassRouter;
  RemoteClassRouter.prototype = Object.create( ClassRouter && ClassRouter.prototype );
  RemoteClassRouter.prototype.constructor = RemoteClassRouter;

    RemoteClassRouter.prototype.staticEvent = function staticEvent (key, value) {
      // Special construction event.
      // Type of `value` is known to be `this.cls` and
      // should be resolved to a brand new instance.
      if (key === '~') {
        // TODO: This won't work with true ES6 classes. What to do...?
        var instance = Object.create(this.cls.prototype);

        // Call `Base`.
        Base.call(instance);

        // Set the local properties to those received.
        assign(instance[internal].props, value.props);

        // Set the id, and track it.
        instance.id = Promise.resolve(value.id);
        instance.ready = Promise.resolve(instance);
        instance[internal].id = value.id;
        this.instances[value.id] = instance;

        this.cls.emit(key, instance);
      } else {
        ClassRouter.prototype.staticEvent.call(this, key, value);
      }
    };

  return RemoteClassRouter;
}(ClassRouter));


// LocalClassRouter is a router for the `Local` class.
// It keeps track of the instances exposed through it.
var LocalClassRouter = (function (ClassRouter) {
  function LocalClassRouter() {
    ClassRouter.apply(this, arguments);

    // TODO: this won't work for multiple bridges
    this.cls.instances = this.instances;

    if (!this.cls.hasOwnProperty('routers')) {
      this.cls.routers = Object.create(null);
    }

    this.cls.routers[this.bridge.id] = this;
  }

  if ( ClassRouter ) LocalClassRouter.__proto__ = ClassRouter;
  LocalClassRouter.prototype = Object.create( ClassRouter && ClassRouter.prototype );
  LocalClassRouter.prototype.constructor = LocalClassRouter;


  // Bind the instance to this bridge, and set its interface name.
  LocalClassRouter.prototype.bindInstance = function bindInstance (id, instance) {
    instance.instances = this.instances;
    instance.bridge = this.bridge;
    instance.interfaceName = this.name;

    // Track the instance.
    this.instances[id] = instance;
  };


  // Adds the given local instance to the router,
  // and informs the other end of it's existence.
  LocalClassRouter.prototype.addInstance = function addInstance (instance) {
    var id = instance[internal].id;

    if (instance.bridge) {
      throw new Error(((this.name) + " instance '" + id + "' is already connected to a bridge."));
    }

    this.bindInstance(id, instance);

    // Inform the other end of the instance's existence.
    this.bridge.notify(this.name + '!', ['~', instance[internal]]);

    return this;
  };


  LocalClassRouter.prototype.constructInstance = function constructInstance (plainArgs) {
    var this$1 = this;

    // Safely, construct an instance from the given arguments.
    return unmarshalAndTypeCheckArguments(this.cls, plainArgs)
      .then( function (args) { return promisedConstruct(this$1.cls, args); })
      .then( function (instance) {
        var ref = instance[internal];
        var id = ref.id;
        var props = ref.props;

        this$1.bindInstance(id, instance);

        // Once the instance is initialized, pass on it's representation.
        return instance.ready.then(function () { return ({ id: id, props: mapValues(props, internalIds) }); });
      });
  };


  LocalClassRouter.prototype.destroyInstance = function destroyInstance (instance) {
    delete this.instances[instance[internal].id];
    instance[internal].id = null;
    instance.id = Promise.reject(new WisperError(domain.RemoteObject,
      code.invalidInstance, 'The instance has been destroyed.'));

    instance.emit('destroy');
  };


  LocalClassRouter.prototype.instanceMethod = function instanceMethod (instance, method, plainArgs) {
    var func = this.cls.prototype[method];

    if (typeof func !== 'function' || func.secret) {
      return Promise.reject(new WisperError(domain.RemoteObject,
        code.missingProcedure,
        ("'" + (this.name) + "' instances have no method '" + method + "'.")));
    }

    return unmarshalAndTypeCheckArguments(func, plainArgs)
      .then( function (args) { return promisedApply(func, instance, args); });
  };

  return LocalClassRouter;
}(ClassRouter));

function createClassRouter(bridge, name, cls) {
  // Constructor be called several times on same object.
  EventHandler.call(cls);

  return ClassRouter.routing(bridge, name, cls);
};

// Decorator for `Remote` and `Local` classes.
// Exposes the classes through the bridge.
function interfaceName(bridge, name) {
  return function (cls) {
    var router = createClassRouter(bridge, name, cls);

    if (!bridge.expose(name, router)) {
      throw new Error(("Route '" + name + "' already exposed."));
    }
  };
}

/**
 * Casts `value` to `identity` if it's not a function.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {Function} Returns cast function.
 */
function castFunction(value) {
  return typeof value == 'function' ? value : identity;
}

/**
 * Iterates over own enumerable string keyed properties of an object and
 * invokes `iteratee` for each property. The iteratee is invoked with three
 * arguments: (value, key, object). Iteratee functions may exit iteration
 * early by explicitly returning `false`.
 *
 * @static
 * @memberOf _
 * @since 0.3.0
 * @category Object
 * @param {Object} object The object to iterate over.
 * @param {Function} [iteratee=_.identity] The function invoked per iteration.
 * @returns {Object} Returns `object`.
 * @see _.forOwnRight
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.forOwn(new Foo, function(value, key) {
 *   console.log(key);
 * });
 * // => Logs 'a' then 'b' (iteration order is not guaranteed).
 */
function forOwn(object, iteratee) {
  return object && baseForOwn(object, castFunction(iteratee));
}

// Creates a property descriptor from a type and key.
function propertyDescriptor(cls, key, type) {
  var descriptor = {
    enumerable: true,
    set: noop,
    get: function get() {
      // Return the value from the internal properties.
      return this[internal].props[key];
    }
  };

  if (type.writable) {
    descriptor.set = function (value) {
      // If the value type-checks, set it.
      if (type.valid(value)) {
        this[internal].props[key] = value;

        // If a bridge exists, notify of the change.
        if (this.bridge) {
          this.dispatch(key, type.marshal(value));
        }
      }
    };
  }

  return descriptor;
}



// The `Properties` decorator adds properties to the prototype
// of the decorated class. It automatically dispatches events
// over RPC whenever these properties are modified.
function properties(definitions, maybeCls) {
  var decorator = function (cls) {
    var inheritedInternal = cls.prototype[internal],
      inheritedDefinitions = inheritedInternal && inheritedInternal.props;

    if (inheritedDefinitions) {
      Object.keys(definitions).forEach(function (name) {
        if (inheritedDefinitions.hasOwnProperty(name)) {
          throw new Error(("Can't redefine inherited property '" + name + ": " + (inheritedDefinitions[name].name) + "'"));
        }
      });
    }

    // Setup default values for properties on prototype.
    Object.defineProperty(cls.prototype, internal, {
      writable: true,
      value: {}
    });

    Object.defineProperty(cls.prototype[internal], 'props', {
      enumerable: true,
      // Extend property definitions with inherited definitions.
      value: assign(definitions, inheritedDefinitions)
    });

    forOwn(definitions, function (type, key) {
      Object.defineProperty(cls.prototype, key, propertyDescriptor(cls, key, type));
    });
  };

  if (maybeCls) {
    return decorator(maybeCls);
  }

  return decorator;
}

/**
 * Base-class for Remotes.
 * Remotes have the following key properties, which shouldn't be overriden:
 *
 * On each instance:
 *   id:            Promise<string>
 *   ready:         Promise<this>
 *
 * On the prototype:
 *   bridge:        wisper.Bridge
 *   interfaceName: string
 *
 * On the class:
 *   instances:     Object<string, instance>
 */
var Remote = (function (Base) {
  function Remote() {
    var this$1 = this;
    var args = [], len = arguments.length;
    while ( len-- ) args[ len ] = arguments[ len ];

    Base.call(this);
    this.id = this.bridge.invoke(this.interfaceName + '~', args).then(function (result) {
      var id;

      // Usually, the result of a construction event
      // should be an { id, props } object.
      if (typeof result === 'object') {
        id = this$1[internal].id = result.id;
        assign(this$1[internal].props, result.props);
      } else {
        // To support older implementations, result can also be just an id.
        id = result;
      }

      // Set the local properties to those received.
      this$1.constructor.instances[id] = this$1;
      return id;
    });

    this.ready = this.id.then(function () { return this$1; });
  }

  if ( Base ) Remote.__proto__ = Base;
  Remote.prototype = Object.create( Base && Base.prototype );
  Remote.prototype.constructor = Remote;

  /**
   * Destroy the corresponding remote object at native end and
   * remove the instance from remoteObjectInstances collection.
   */
  Remote.prototype.destroy = function destroy () {
    var this$1 = this;

    this.id.then(function (id) {
      // Ensure `destroy` is only called once.
      if (this$1[internal].id !== id) { return; }
      this$1[internal].id = null;

      this$1.bridge.notify(this$1.interfaceName + ':~', [id]);
      delete this$1.constructor.instances[id];
    });
  };

  return Remote;
}(Base));

// Decorator to mark methods of an exported class private, or `secret`.
function secret(target, key, descriptor) {
  descriptor.value.secret = true;
}

export { types, signature, BaseBridge, IframeBridge, PropertyBridge, Namespace, split, Base, Local, Remote, properties, secret, interfaceName, createClassRouter };